import { Task } from '../tasks/Task.interface';
import { FetchError } from '../../common/api/api';
import { InlineSelectOptions } from '../../common/inline/InlineSelect';
import { Attachment } from '../../common/attachment/Attachment.interface';
import { SurveyOptionalBoolean } from '../../common/SurveyOptionalBoolean';
import { User } from '../../user/User.interface';
import { JiraFieldConfig } from '../jira-server/jira-server.interface';

export interface SupplierConfig {
  visibleJiraFields?: JiraFieldConfig[];
}

export enum SupplierReviewDate {
  M6 = '6 Months',
  Y1 = '1 Year',
  Y2 = '2 Years'
}

export const SupplierReviewDateOptions: InlineSelectOptions[] = [
  { value: '', label: '-- No Review Date --' },
  ...Object.keys(SupplierReviewDate).map((value: string) => {
    const key = value as keyof typeof SupplierReviewDate;
    return { value, label: SupplierReviewDate[key] };
  })
];

export interface Supplier {
  id: number;
  taskId: number;
  task: Task;
  version: number;
  companyName: string;
  contactName?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  numberOfLocations?: number;
  primaryLocation?: string;
  numberOfEmployees?: number;
  productServices?: string;
  hasQualityProgram?: SurveyOptionalBoolean;
  qualityProgramBasedOn?: string;
  seperateFromProductionDepartment?: SurveyOptionalBoolean;
  seperateFromProductionDepartmentDescription?: string;
  hasCurrentQualityManual?: SurveyOptionalBoolean;
  hasOrganizationChart?: SurveyOptionalBoolean;
  hasTechnicalDataControl?: SurveyOptionalBoolean;
  hasDocumentRetentionControl?: SurveyOptionalBoolean;
  hasTrainingLogs?: SurveyOptionalBoolean;
  hasShelfLifeProgram?: SurveyOptionalBoolean;
  hasQualityControl?: SurveyOptionalBoolean;
  hasReceivingInspectionProcedures?: SurveyOptionalBoolean;
  hasCalibrationProgram?: SurveyOptionalBoolean;
  hasRevisions?: SurveyOptionalBoolean;
  qualityReviewProcedure?: string;
  isQualityManualAvailable?: SurveyOptionalBoolean;
  attachments?: any[];
  hasPassFailCriteria?: SurveyOptionalBoolean;
  hasMeasurementVerification?: SurveyOptionalBoolean;
  hasMeasurementResults?: SurveyOptionalBoolean;
  hasQualityEvidenceProgram?: SurveyOptionalBoolean;
  hasNonconformanceProgram?: SurveyOptionalBoolean;
  hasInternalAuditProgram?: SurveyOptionalBoolean;
  noInternalAuditProgramExplain?: string;
  internalAuditSchedule?: string;
  hasCustomerComplaintProgram?: SurveyOptionalBoolean;
  hasRootCauseAnalysisProgram?: SurveyOptionalBoolean;
  hasCorrectiveActionProgram?: SurveyOptionalBoolean;
  hasCorrectiveActionProgramRetrospective?: SurveyOptionalBoolean;
  nextReviewDate?: SupplierReviewDate | null;
  completed?: Date;
  sentDate?: Date;
  reviewDate?: Date | null;
  created?: Date;
  createdBy?: User;
  modified?: Date;
  modifiedBy?: User;
}

export interface SupplierVersion
  extends Omit<Supplier, 'modified' | 'modifiedBy'> {
  supplierId: number;
}

export interface BaseSupplierCardProps {
  supplier: Supplier;
  token?: string;
  supplierVersion?: SupplierVersion;
  onChange?: (supplier: Partial<Supplier>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
}

export interface SupplierPurchaseOrder {
  id: number;
  taskId?: number;
  task?: Task;
  requesterName?: string;
  requesterPhoneNumber?: string;
  requesterDepartment?: string;
  supplierId?: number;
  supplier?: Supplier;
  quote?: OrderQuoteItem[];
  attachments?: Attachment[];
  taxes?: number;
  fees?: number;
  shipping?: number;
  totalCost?: number;
  orderDate?: Date;
  created?: Date;
  createdBy?: User;
  modified?: Date;
  modifiedBy?: User;
}

export type SupplierPurchaseOrderCreateDto = Omit<SupplierPurchaseOrder, 'id'>;

export interface OrderQuoteItem {
  code: string;
  description: string;
  quantity: number;
  price: number;
}

export interface BaseSporCardProps {
  spor: SupplierPurchaseOrder;
  onChange?: (spor: Partial<SupplierPurchaseOrder>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
}
